import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./lib/auth";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { FloatingCallButton } from "./components/PhoneCTA";
import ChatWidget from "./components/ChatWidget";
import Home from "./pages/Home";
import Library from "./pages/Library";
import TherapyDetail from "./pages/TherapyDetail";
import Goals from "./pages/Goals";
import GoalDetail from "./pages/GoalDetail";
import Questionnaire from "./pages/Questionnaire";
import Bloodwork from "./pages/Bloodwork";
import LocationsDirectory from "./pages/LocationsDirectory";
import LocationSlugRouter from "./components/LocationSlugRouter";
import About from "./pages/About";
import Contact from "./pages/Contact";
import HowItWorks from "./pages/HowItWorks";
import RequestConsultation from "./pages/RequestConsultation";
import MedicalConsultation from "./pages/MedicalConsultation";
import Telemedicine from "./pages/Telemedicine";
import ResearchLibrary from "./pages/ResearchLibrary";
import ProviderFAQ from "./pages/ProviderFAQ";
import { useAuth as _authForReview } from "./lib/auth";
import { ReviewProvider } from "./components/review/ReviewContext";
import ReviewLayer from "./components/review/ReviewLayer";
import ReviewCommentsPanel from "./components/review/ReviewCommentsPanel";
import ReferencesLibrary from "./pages/admin/ReferencesLibrary";
import CmsPage from "./pages/CmsPage";
import ProvidersDirectory from "./pages/ProvidersDirectory";
import LocalPeptideGuidesHub from "./pages/LocalPeptideGuidesHub";
import Testimonials from "./pages/Testimonials";
import {
  CalculatorsIndex, BmiCalculator, BmrCalculator, TdeeCalculator,
  WaistHeightCalculator, ProteinCalculator,
} from "./pages/calculators";
import { useParams } from "react-router-dom";

/**
 * Known state slugs that should route to `/providers/{state}` list view
 * rather than to an individual provider profile. Kept small & explicit —
 * expanded by the internal_linking sync when new state pages are seeded.
 * If in doubt, prefer the individual provider profile route.
 */
const STATE_SLUGS = new Set([
  "alabama","alaska","arizona","arkansas","california","colorado","connecticut",
  "delaware","florida","georgia","hawaii","idaho","illinois","indiana","iowa",
  "kansas","kentucky","louisiana","maine","maryland","massachusetts","michigan",
  "minnesota","mississippi","missouri","montana","nebraska","nevada","new-hampshire",
  "new-jersey","new-mexico","new-york","north-carolina","north-dakota","ohio",
  "oklahoma","oregon","pennsylvania","rhode-island","south-carolina","south-dakota",
  "tennessee","texas","utah","vermont","virginia","washington","west-virginia",
  "wisconsin","wyoming",
]);

/** Resolves /providers/:slug — either a state directory or an individual profile. */
function ProvidersSlugRoute() {
  const { slug } = useParams();
  if (STATE_SLUGS.has(slug)) {
    return <ProvidersDirectory stateFilter={slug} />;
  }
  return <CmsPage pageType="provider" />;
}
import ConditionResolver from "./pages/ConditionResolver";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import ThankYou from "./pages/ThankYou";
import LegalPage from "./pages/LegalPage";
import Pricing from "./pages/Pricing";
import FindMyPeptide from "./pages/FindMyPeptide";
import AttributionListener from "./components/AttributionListener";
import StickyConsultationBar from "./components/StickyConsultationBar";
import ExitIntentModal from "./components/ExitIntentModal";
import SemLandingLayout from "./components/SemLandingLayout";

function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <FloatingCallButton />
      <StickyConsultationBar />
      <ExitIntentModal />
      <ChatWidget />
    </div>
  );
}

function ProtectedAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-stone-500">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

/** Draft-preview shell — reuses CmsPage renderer, forces `preview` mode which
 *  routes the fetch through the authenticated admin endpoint. Displays a
 *  prominent "DRAFT — not publicly visible" banner. */
function DraftPreviewShell() {
  const { pageType, slug } = useParams();
  const { user } = _authForReview();
  return (
    <div className="min-h-screen bg-stone-50">
      <div className="sticky top-0 z-40 bg-amber-100 border-b border-amber-300 text-amber-900 text-sm text-center py-2 px-4" data-testid="draft-preview-banner">
        <strong>DRAFT PREVIEW</strong> — This content is <em>not</em> public, not indexed, and no reviewer name will appear until you approve it.
        Slug: <code>/{pageType === "article" ? "articles" : pageType}/{slug}</code>
      </div>
      <ReviewProvider pageType={pageType} slug={slug} user={user}>
        <div className="flex flex-col lg:flex-row items-start">
          <div className="w-full flex-1 min-w-0">
            <CmsPage pageType={pageType} preview />
          </div>
          <ReviewCommentsPanel />
        </div>
        <ReviewLayer />
      </ReviewProvider>
    </div>
  );
}

export default function App() {
  // Hydration-ready marker for the Puppeteer prerender pipeline.
  // scripts/prerender.js waits for this attribute before capturing the
  // <html> snapshot to guarantee React finished its initial commit.
  useEffect(() => {
    document.documentElement.setAttribute("data-app-ready", "1");
  }, []);
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AttributionListener />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/peptide-library" element={<Library />} />
              <Route path="/peptide-library/:slug" element={<TherapyDetail />} />
              <Route path="/peptides/:slug" element={<TherapyDetail />} />
              <Route path="/treatment-goals" element={<Goals />} />
              <Route path="/treatment-goals/:slug" element={<GoalDetail />} />
              <Route path="/conditions" element={<Goals />} />
              <Route path="/conditions/:slug" element={<ConditionResolver />} />
              <Route path="/find-my-treatment" element={<Questionnaire />} />
              <Route path="/telemedicine" element={<Telemedicine />} />
              <Route path="/research" element={<ResearchLibrary />} />
              <Route path="/provider-faq" element={<ProviderFAQ />} />
              <Route path="/bloodwork" element={<Bloodwork />} />
              <Route path="/locations" element={<LocationsDirectory />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/request-consultation" element={<RequestConsultation />} />
              {/* Legacy consultation routes — permanently redirect into the single canonical form. */}
              <Route path="/schedule" element={<Navigate to="/request-consultation" replace />} />
              <Route path="/consultation" element={<Navigate to="/request-consultation" replace />} />
              {/* Legacy peptide-library slug redirects — permanent canonical rename (Batch 2A). */}
              <Route path="/peptide-library/retatrutide" element={<Navigate to="/peptide-library/retatrutide-glp-3" replace />} />
              <Route path="/peptide-library/glow" element={<Navigate to="/peptide-library/glow-blend-ghk-cu-bpc-157" replace />} />
              <Route path="/peptide-library/klow" element={<Navigate to="/peptide-library/klow-blend-ghk-cu-kpv-bpc-157-tb-500" replace />} />
              <Route path="/peptide-library/selank-semax" element={<Navigate to="/peptide-library/selank-semax-blend" replace />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/find-my-peptide" element={<FindMyPeptide />} />
              <Route path="/thank-you" element={<ThankYou />} />
              {/* Legal pages — hybrid CMS-driven, hardcoded fallback (see /app/frontend/src/data/legalContent.js) */}
              <Route path="/privacy-policy"    element={<LegalPage slug="privacy-policy" />} />
              <Route path="/terms-of-use"      element={<LegalPage slug="terms-of-use" />} />
              <Route path="/telehealth-consent" element={<LegalPage slug="telehealth-consent" />} />
              <Route path="/medical-disclaimer" element={<LegalPage slug="medical-disclaimer" />} />
              {/* CMS-driven dynamic routes (see /app/backend/cms_pages.py) */}
              <Route path="/locations/:slug" element={<LocationSlugRouter />} />
              <Route path="/locations/:slug/:city" element={<CmsPage pageType="city" />} />
              <Route path="/symptoms/:slug" element={<CmsPage pageType="symptom" />} />
              <Route path="/providers" element={<ProvidersDirectory />} />
              <Route path="/providers/:slug" element={<ProvidersSlugRoute />} />
              <Route path="/compare/:slug" element={<CmsPage pageType="comparison" />} />
              <Route path="/research-library/:slug" element={<CmsPage pageType="research" />} />
              <Route path="/articles/local-peptide-guides" element={<LocalPeptideGuidesHub />} />
              <Route path="/articles/local-peptide-guides/:market" element={<LocalPeptideGuidesHub />} />
              <Route path="/articles/:slug" element={<CmsPage pageType="article" />} />
              <Route path="/faq/:slug" element={<CmsPage pageType="faq" />} />
              <Route path="/calculators" element={<CalculatorsIndex />} />
              <Route path="/calculators/bmi" element={<BmiCalculator />} />
              <Route path="/calculators/bmr" element={<BmrCalculator />} />
              <Route path="/calculators/tdee" element={<TdeeCalculator />} />
              <Route path="/calculators/waist-to-height" element={<WaistHeightCalculator />} />
              <Route path="/calculators/daily-protein" element={<ProteinCalculator />} />
              <Route path="/testimonials" element={<Testimonials />} />
            </Route>
            {/* Google Ads SEM landing page — lean layout (no full nav, no
                sticky bar, no exit-intent, no chat widget). Excluded from
                sitemap. noindex,nofollow enforced by the page itself. */}
            <Route element={<SemLandingLayout />}>
              <Route path="/medical-consultation" element={<MedicalConsultation />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedAdmin><AdminDashboard /></ProtectedAdmin>} />
            <Route path="/admin/references" element={<ProtectedAdmin><ReferencesLibrary /></ProtectedAdmin>} />
            {/* Authenticated draft-preview route — reviewers see drafts as they'll render publicly */}
            <Route path="/admin/preview/:pageType/:slug" element={<ProtectedAdmin><DraftPreviewShell /></ProtectedAdmin>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
